// Minimal promisified IndexedDB wrapper for the generation history store.
//
// Kept dependency-free (the project has no idb/dexie/localforage) and tiny: a
// single database with one object store keyed by generation id and indexed by
// userId, so history can be listed / cleared per logged-in user. Every public
// helper resolves to a safe fallback when IndexedDB is unavailable (private
// mode, disabled, or a blocked upgrade) so history degrades to "not saved"
// rather than throwing — the same defensive posture as apiKey.ts.

import { MagicGeneration } from './types';

const DB_NAME = 'assetLab.magicStudio';
const DB_VERSION = 1;
const STORE = 'generations';
const USER_INDEX = 'userId';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex(USER_INDEX, 'userId', { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  // If opening fails, drop the cached rejection so a later call can retry.
  dbPromise.catch(() => {
    dbPromise = null;
  });
  return dbPromise;
}

// Resolves with the request result, or rejects with its error.
function promisifyRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// All records for a user, newest first. Returns [] if storage is unavailable.
export async function dbList(userId: string): Promise<MagicGeneration[]> {
  try {
    const db = await openDb();
    const tx = db.transaction(STORE, 'readonly');
    const index = tx.objectStore(STORE).index(USER_INDEX);
    const records = await promisifyRequest(index.getAll(IDBKeyRange.only(userId)));
    return (records as MagicGeneration[]).sort((a, b) => b.createdAt - a.createdAt);
  } catch {
    return [];
  }
}

// One record by id, or undefined if missing / storage unavailable.
export async function dbGet(id: string): Promise<MagicGeneration | undefined> {
  try {
    const db = await openDb();
    const tx = db.transaction(STORE, 'readonly');
    const record = await promisifyRequest(tx.objectStore(STORE).get(id));
    return (record as MagicGeneration | undefined) ?? undefined;
  } catch {
    return undefined;
  }
}

// Insert a record. Rethrows so the caller can react to quota errors; swallows
// the "storage unavailable" case silently (nothing to react to).
export async function dbAdd(record: MagicGeneration): Promise<void> {
  let db: IDBDatabase;
  try {
    db = await openDb();
  } catch {
    return;
  }
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(record);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

export async function dbRemove(id: string): Promise<void> {
  try {
    const db = await openDb();
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(id);
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // ignore — nothing else we can do if storage is unavailable.
  }
}

// Delete every record for a user.
export async function dbClear(userId: string): Promise<void> {
  try {
    const db = await openDb();
    const tx = db.transaction(STORE, 'readwrite');
    const index = tx.objectStore(STORE).index(USER_INDEX);
    const keys = await promisifyRequest(index.getAllKeys(IDBKeyRange.only(userId)));
    const store = tx.objectStore(STORE);
    keys.forEach((key) => store.delete(key));
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // ignore
  }
}
